import {
	ClientSociety,
	ClientUser,
	PAGES,
	SanitizedWarrantySelection,
} from "@dune/types";
import { convertUndefinedToNull } from "@dune/utils";
import { getErrorMessage } from "@dune/vars";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ConstructionSiteDetailsFormData } from "../../component/brokers/constructionSites/useConstructionSiteDetailsForm";
import { CreateConstructionSiteClientFormData } from "../../component/brokers/constructionSites/useCreateConstructionSiteForm";
import { buildConstructionSiteClientDto } from "../../utils/constructionSiteClient.utils";
import { BACKEND_URL } from "../../environment/environment";
import { privateAxios } from "../axios";
import { constructionSitesQueryKeys } from "../query-keys/constructionSites.query-keys";

export const createConstructionSiteClient = async (
	constructionSiteClientData: CreateConstructionSiteClientFormData & {
		isGFA?: boolean;
		brokerSocietyId?: number;
		actions?: unknown;
	},
) => {
	const dataToSend = buildConstructionSiteClientDto(constructionSiteClientData);

	const { data } = await privateAxios.post<{
		client: ClientUser[] | ClientSociety | null;
		moaClient: ClientUser | ClientSociety | null;
		constructionSiteId: number;
	}>(
		`${BACKEND_URL}/constructionSites/createClient`,
		convertUndefinedToNull(dataToSend),
	);
	return data;
};

export const updateConstructionSiteDetails = async (params: {
	constructionSiteId?: number;
	clientType?: "USER" | "SOCIETY";
	clientIds?: number[];
	clientIsMOA?: boolean;
	moaClientType: "USER" | "SOCIETY";
	moaClientId?: number;
	constructionSiteDetailsDto: ConstructionSiteDetailsFormData;
	action: "validate" | "save";
	hasChanges?: boolean;
	isGFA?: boolean;
	brokerSocietyId?: number;
}) => {
	const { throughSyndic, ...constructionSiteDataWithoutClientDetails } =
		params.constructionSiteDetailsDto;

	const {
		hasUserAccess,
		userAccess: _userAccess,
		...syndicSocietyWithoutUserAccess
	} = "syndicSociety" in params.constructionSiteDetailsDto
		? params.constructionSiteDetailsDto.syndicSociety
		: {};

	let dataToSend = {
		...constructionSiteDataWithoutClientDetails,
		throughSyndic,
		...(throughSyndic &&
			(hasUserAccess === "true"
				? {
						syndicSociety:
							"syndicSociety" in params.constructionSiteDetailsDto
								? {
										...params.constructionSiteDetailsDto.syndicSociety,
										isSyndic: true,
									}
								: undefined,
					}
				: {
						syndicSociety: {
							...syndicSocietyWithoutUserAccess,
							hasUserAccess,
							isSyndic: true,
						},
					})),
	};
	const taxMode = constructionSiteDataWithoutClientDetails.taxMode;

	if (taxMode === "HT") {
		const { totalAmountTTC: _totalAmountTTC, ...dataWithoutTTC } = dataToSend;
		dataToSend = dataWithoutTTC;
	} else {
		const { totalAmountHT: _totalAmountHT, ...dataWithoutHT } = dataToSend;
		dataToSend = dataWithoutHT;
	}
	const { data } = await privateAxios.post(
		`${BACKEND_URL}/constructionSites/${params.action}-details`,
		{
			constructionSiteId: params.constructionSiteId,
			clientType: params.clientType,
			clientIds: params.clientIds,
			clientIsMOA: params.clientIsMOA,
			moaClientType: params.moaClientType,
			moaClientId: params.moaClientId,
			constructionSiteDetailsDto: {
				...convertUndefinedToNull(dataToSend),
			},
			hasChanges: params.hasChanges,
			isGFA: params.isGFA,
			...(params.brokerSocietyId && {
				brokerSocietyId: params.brokerSocietyId,
			}),
		},
	);
	return data;
};

export const useValidateConstructionSiteDetails = () => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: updateConstructionSiteDetails,
		onSuccess: async (data, variables) => {
			toast.success("Les opérations ont bien été remplies");

			if (variables.constructionSiteId) {
				await queryClient.invalidateQueries({
					queryKey: constructionSitesQueryKeys.detail(
						variables.constructionSiteId.toString(),
					),
					refetchType: "active",
				});
			}

			navigate(PAGES.PROJECTS.binders.show.withParams(data.id));
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la validation des opérations", {
				description: errorMessage,
			});
		},
	});
};

export const useSaveConstructionSiteDetails = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: updateConstructionSiteDetails,
		onSuccess: async (_data, variables) => {
			toast("Les opérations ont bien été sauvegardées");
			if (variables.constructionSiteId) {
				await queryClient.invalidateQueries({
					queryKey: constructionSitesQueryKeys.detail(
						variables.constructionSiteId.toString(),
					),
					refetchType: "active",
				});
			}
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la sauvegarde des opérations", {
				description: errorMessage,
			});
		},
	});
};

export const updateConstructionSiteOffers = async (params: {
	id: string;
	binderWarranties: SanitizedWarrantySelection[];
	withDocumentsGeneration?: boolean;
}) => {
	const { binderWarranties, withDocumentsGeneration } = params;
	const { data } = await privateAxios.post(
		`${BACKEND_URL}/constructionSites/${params.id}/offers`,
		{ binderWarranties, withDocumentsGeneration },
	);
	return data;
};

export const useUpdateConstructionSiteOffers = (constructionSiteId: string) => {
	const navigate = useNavigate();
	return useMutation({
		mutationFn: updateConstructionSiteOffers,
		onSuccess: () => {
			toast("Les opérations ont bien été remplies");
			navigate(PAGES.PROJECTS.show.withParams(constructionSiteId));
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la modification des opérations", {
				description: errorMessage,
			});
		},
	});
};

export const updateConstructionSiteBrokerSociety = async (params: {
	id: string;
	brokerSocietyId: number;
}): Promise<{ success: boolean }> => {
	const { data } = await privateAxios.patch<{ success: boolean }>(
		`${BACKEND_URL}/constructionSites/${params.id}/broker-society`,
		{ brokerSocietyId: params.brokerSocietyId },
	);
	return data;
};

export const useUpdateConstructionSiteBrokerSociety = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: updateConstructionSiteBrokerSociety,
		onSuccess: (_data, variables) => {
			toast.success("L'apporteur a bien été modifié");
			queryClient.invalidateQueries({
				queryKey: constructionSitesQueryKeys.detail(variables.id),
				refetchType: "active",
			});
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la modification de l'apporteur", {
				description: errorMessage,
			});
		},
	});
};

export const toggleSkipConstructionSite = async (params: {
	id: string;
	skipReason?: string;
}) => {
	const { data } = await privateAxios.put(
		`${BACKEND_URL}/constructionSites/${params.id}/skip`,
		{ skipReason: params.skipReason },
	);
	return data;
};

export const useToggleSkipConstructionSite = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: toggleSkipConstructionSite,
		onSuccess: (_data, variables) => {
			if (variables.skipReason) {
				toast("Le chantier a été passé sans suite");
			} else {
				toast("Le chantier a été remis en cours");
			}
			queryClient.invalidateQueries({
				queryKey: constructionSitesQueryKeys.detail(variables.id),
				refetchType: "active",
			});
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la modification du statut du chantier", {
				description: errorMessage,
			});
		},
	});
};
